// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import { loadPromotions } from '../../../actions/promotionActions';
import { NavLink, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { hexToRgbA } from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import { useTranslation } from 'react-i18next';

export interface PromotionsListProps {
  reload?: any;
  keyProviders?: any[];
  territories?: any[];
  scope?: string;
}

function PromotionsList({ reload, keyProviders, scope }: PromotionsListProps) {
  const location = useLocation();
  const allColumns = [
    'name',
    'keyProviderName',
    'start',
    'end',
    'numberOfSKUs',
  ];
  const defaultVisibleColumns = allColumns.filter((x) => x !== 'todo');
  const { t } = useTranslation();
  const [promotions, setPromotions] = useState<any>();
  const params = new URLSearchParams(location.search);
  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'promotionlist-settings',
      getTableSettingsDefaults({
        defaultSort: 'start',
        defaultVisibleColumns,
      }),
      null,
      loadPromotions,
      setPromotions,
      reload,
      t('Failed to load promotions'),
      scope
    );

  const columnsConfig = [
    {
      Key: 'name',
      Label: t('Promotion'),
      className: 'flush',
      Render: (item: any) => (
        <div
          className="p-2 d-flex align-items-center promotion-td"
          style={
            item.keyProviderColourHex
              ? {
                  borderLeft: `3px solid ${item.keyProviderColourHex}`,
                }
              : undefined
          }
        >
          <NavLink to={`/promotion/${item.id}`}>{item.name}</NavLink>
        </div>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyProviderName',
      Label: t('Key provider'),
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
          <select
            className={`form-control ${value ? 'filtered' : ''}`}
            value={value}
            onChange={(el) => filterChange(col.Key, (el.target as HTMLSelectElement).value)}
          >
            <option value="">{t('--- All ---')}</option>
            {keyProviders &&
              [...keyProviders]
                ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
                ?.map((x: any) => (
                  <option key={x.id} value={x.name}>
                    {x.name}
                  </option>
                ))}
          </select>
          );
        },
      },
    },
    {
      Key: 'start',
      Label: t('Start'),
      Render: (item: any) => <FormattedDate value={item.start} variant="default" />,
    },
    {
      Key: 'end',
      Label: t('End'),
      Render: (item: any) => <FormattedDate value={item.end} variant="default" />,
    },
    {
      Key: 'numberOfSKUs',
      Label: t('Number of SKUs'),
      Render: (item: any) => item.numberOfSKUs,
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig: any[] = [];
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return promotions ? (
    <KwPanel>
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        rows={promotions.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={promotions.totalRowCount}
        filteredRowCount={promotions.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filterChange={filterChange}
        trStyle={(row: any) => ({
          backgroundColor: `${hexToRgbA(row.keyProviderColourHex, 0.0375)}`,
        })}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default PromotionsList;
